/**
 * Workflow to Graph Conversion
 *
 * Converts WorkflowAgent API data to React Flow graph format.
 * This is a pure function - the caller is responsible for memoization if needed.
 */

import { ConditionalStep, Step, StepType, WorkflowAgent } from '@frontend/types/workflow-agent';

import {
  ConditionBranchNode,
  ConditionNodeData,
  StepNode,
  StepWithPosition,
  TriggerNode,
  WorkflowEdge,
  WorkflowGraph,
  WorkflowNode,
} from '../types';
import { EdgeMetadata, NodeMetadata, WorkflowMetadata } from '../types/api';

import { generateConditionNodeId, generateEdgeId } from './workflow-id-utils';

/**
 * Create a standard workflow edge.
 */
function createStandardEdge(
  id: string,
  source: string,
  target: string,
  sourceHandle?: string | null,
  targetHandle?: string | null
): WorkflowEdge {
  return {
    id,
    source,
    target,
    sourceHandle: sourceHandle ?? undefined,
    targetHandle: targetHandle ?? undefined,
    deletable: true,
    type: 'custom',
  };
}

/**
 * Convert workflow agent API data to React Flow graph format.
 *
 * @param steps - The steps from the workflow agent
 * @param triggers - The triggers from the workflow agent
 * @param startStepId - The ID of the starting step
 * @param metadata - The workflow metadata containing node positions and edge info
 * @returns A WorkflowGraph with typed nodes and edges
 */
export function convertWorkflowToGraph(
  steps: Record<string, Step>,
  triggers: WorkflowAgent['triggers'],
  startStepId: string,
  metadata: WorkflowMetadata
): WorkflowGraph {
  // Metadata lookup functions
  const getMetadataNode = (nodeId: string): NodeMetadata | undefined => {
    return metadata.nodes.find(node => node.id === nodeId);
  };

  const getMetadataEdge = (source: string, target: string): EdgeMetadata | undefined => {
    return metadata.edges?.find(edge => edge.source === source && edge.target === target);
  };

  /**
   * Create trigger nodes from workflow triggers.
   */
  function createTriggerNodes(workflowTriggers: WorkflowAgent['triggers']): TriggerNode[] {
    const triggerNodes: TriggerNode[] = [];

    if (!workflowTriggers || workflowTriggers.length === 0) {
      return triggerNodes;
    }

    workflowTriggers.forEach(trigger => {
      const triggerNodeId = trigger.id;
      const triggerMetadata = getMetadataNode(triggerNodeId);

      // Build trigger node - cast needed because object literal with dynamic type
      // doesn't preserve discriminated union narrowing
      const triggerNode = {
        id: triggerNodeId,
        type: trigger.type,
        position: triggerMetadata?.position || { x: 0, y: 0 },
        data: trigger,
        measured: triggerMetadata?.measured,
      } as TriggerNode;

      triggerNodes.push(triggerNode);
    });

    return triggerNodes;
  }

  /**
   * Create a step node from step data.
   */
  function createStepNode(step: Step, parentId?: string): StepNode {
    const metadataNode = getMetadataNode(step.step_id);

    // Check if step has embedded position (from ForLoop steps)
    let position = metadataNode?.position || { x: 0, y: 0 };

    // Handle position that might be embedded in step data (StepWithPosition)
    const stepWithPosition = step as StepWithPosition;
    if (stepWithPosition.position) {
      const { x, y } = stepWithPosition.position;
      if (x !== undefined && y !== undefined) {
        position = { x, y };
      }
    }

    // Default position for nested nodes
    if (parentId && position.x === 0 && position.y === 0) {
      position = { x: 40, y: 60 };
    }

    // Build the node - cast needed because object literal with dynamic type
    // doesn't preserve discriminated union narrowing
    const stepNode = {
      id: step.step_id,
      type: step.type,
      position,
      data: step,
      parentId,
      extent: parentId ? ('parent' as const) : undefined,
      measured: metadataNode?.measured,
    } as StepNode;

    // Handle ForLoop node dimensions
    if (step.type === StepType.FOR_LOOP) {
      const savedWidth = metadataNode?.measured?.width || metadataNode?.width;
      const savedHeight = metadataNode?.measured?.height || metadataNode?.height;

      const width = savedWidth || (parentId ? 280 : 320);
      const height = savedHeight || (parentId ? 160 : 188);

      stepNode.style = { width, height };
      stepNode.width = width;
      stepNode.height = height;
    }

    return stepNode;
  }

  /**
   * Create condition nodes for a conditional step.
   * These are UI-only nodes for condition branches.
   */
  function createConditionalStepNodes(
    step: ConditionalStep,
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    parentId?: string
  ): void {
    step.conditions.forEach(condition => {
      const conditionNodeId = generateConditionNodeId(step.step_id, condition);
      const conditionMetadata = getMetadataNode(conditionNodeId);

      const conditionData: ConditionNodeData = {
        type: 'condition',
        name: `Condition: ${condition.match_type}`,
        matching_condition: condition.matching_condition,
        match_type: condition.match_type,
        label: `${condition.match_type}: ${condition.matching_condition}`,
      };

      // Create condition branch node with proper typing
      const conditionNode: ConditionBranchNode = {
        id: conditionNodeId,
        type: 'condition',
        position: conditionMetadata?.position || { x: 0, y: 0 },
        data: conditionData,
        parentId,
        extent: parentId ? 'parent' : undefined,
        measured: conditionMetadata?.measured,
      };

      nodes.push(conditionNode);

      // Edge from conditional step to condition node
      const edgeToCondition = getMetadataEdge(step.step_id, conditionNodeId);
      edges.push(
        createStandardEdge(
          edgeToCondition?.id || generateEdgeId(),
          step.step_id,
          conditionNodeId,
          edgeToCondition?.sourceHandle,
          edgeToCondition?.targetHandle
        )
      );

      // Edge from condition node to target step
      const edgeFromCondition = getMetadataEdge(conditionNodeId, condition.go_to_step_id);
      edges.push(
        createStandardEdge(
          edgeFromCondition?.id || generateEdgeId(),
          conditionNodeId,
          condition.go_to_step_id,
          edgeFromCondition?.sourceHandle,
          edgeFromCondition?.targetHandle
        )
      );
    });
  }

  /**
   * Process steps recursively, creating nodes and edges.
   */
  function processSteps(
    stepsToProcess: Record<string, Step>,
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    parentId?: string
  ): void {
    if (!stepsToProcess || Object.keys(stepsToProcess).length === 0) {
      return;
    }

    // Create nodes for all steps
    Object.values(stepsToProcess).forEach(step => {
      const stepNode = createStepNode(step, parentId);
      nodes.push(stepNode);

      // Handle conditional steps - create condition nodes
      if (step.type === StepType.CONDITIONAL) {
        createConditionalStepNodes(step, nodes, edges, parentId);
      }

      // Handle for-loop steps - process nested steps recursively
      if (step.type === StepType.FOR_LOOP) {
        const forLoopSteps = step.steps || {};
        processSteps(forLoopSteps, nodes, edges, step.step_id);
      }
    });

    // Create edges between steps based on next_step_id
    Object.values(stepsToProcess).forEach(step => {
      if (step.next_step_id && step.type !== StepType.CONDITIONAL) {
        if (stepsToProcess[step.next_step_id]) {
          const metadataEdge = getMetadataEdge(step.step_id, step.next_step_id);
          edges.push(
            createStandardEdge(
              metadataEdge?.id || generateEdgeId(),
              step.step_id,
              step.next_step_id,
              metadataEdge?.sourceHandle,
              metadataEdge?.targetHandle
            )
          );
        }
      }
    });
  }

  /**
   * Create edges from triggers to the start step.
   */
  function createTriggerToStartStepEdges(
    workflowTriggers: WorkflowAgent['triggers'],
    startId: string | undefined,
    edges: WorkflowEdge[]
  ): void {
    if (!startId || !workflowTriggers || workflowTriggers.length === 0) {
      return;
    }

    workflowTriggers.forEach(trigger => {
      const metadataEdge = getMetadataEdge(trigger.id, startId);
      edges.push(
        createStandardEdge(
          metadataEdge?.id || generateEdgeId(),
          trigger.id,
          startId,
          metadataEdge?.sourceHandle,
          metadataEdge?.targetHandle
        )
      );
    });
  }

  // Main conversion logic
  const nodes: WorkflowNode[] = [];
  const edges: WorkflowEdge[] = [];

  if (!steps || !triggers) {
    return { nodes, edges };
  }

  // Create trigger nodes
  const triggerNodes = createTriggerNodes(triggers);
  nodes.push(...triggerNodes);

  // Process all steps recursively
  processSteps(steps, nodes, edges);

  // Create edges from triggers to start step
  createTriggerToStartStepEdges(triggers, startStepId, edges);

  return { nodes, edges };
}
